/**
 * «Gestor Individual»: who manages a document (Addendum No. 3, Point 7) and, for an administrator,
 * the way to hand it to another Gestor. Shared by the goods and the route-sheet detail, which differ
 * only in the endpoint the move is sent to — the caller's `reassign`.
 *
 * It shows what it is handed. Who is shown the line at all is the screen's decision — administrators
 * only (design spec §10), so a screen hands every other role `owner={null}` — and `canReassign` is
 * the screen's as well, since the server refuses anyone else's move.
 *
 * Nothing is shown when no Gestor manages the document: a line reading "none" would be noise on every
 * document the company's other users created.
 *
 * A pending replacement draft — a draft that names the document it replaces — is never reassigned on
 * its own: the server refuses it (`REPLACEMENT_DRAFT_FOLLOWS_ORIGINAL`, 2026-10-01), and it moves with
 * its original. So it is not offered, and the line says why in its place.
 *
 * The dialog says what it is waiting for, or why it cannot go on, and never offers a list it does not
 * have: a loading line while the Gestores are read, the error when they cannot be listed, a notice when
 * there is nobody else, and the choice only once there is someone to choose, which then takes the focus.
 * A refusal of the Gestor chosen (`NOT_A_GESTOR`) also reads the list again, as the sentence it shows
 * tells the administrator to.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DecaApiError, type DocumentOwner } from '@deca/api'

import { apiErrorMessage } from '../app/errors.ts'
import { useUsers } from '../app/queries.ts'
import { Button, Datum, Notice } from './primitives.tsx'
import { Dialog } from './Dialog.tsx'
import { SelectInput } from './form.tsx'

export function DocumentOwnerLine({
  owner,
  subject,
  canReassign,
  reassign,
  onReassigned,
}: {
  owner: DocumentOwner | null
  /** The document the line is on — a DeCA or a route sheet, which name what they replace alike. */
  subject: { estado: string; supersedesDocumentId?: string | null | undefined }
  /** A company administrator — `abilities.administer`. The server refuses anyone else. */
  canReassign: boolean
  /** Sends the move to the document's own endpoint; rejects with the API's error. */
  reassign: (userId: string) => Promise<unknown>
  onReassigned: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  if (!owner) return null
  const movesWithOriginal = subject.estado === 'BORRADOR' && Boolean(subject.supersedesDocumentId)

  return (
    <div className="stack-sm">
      <Datum label={t('owner.label')} value={owner.display} />
      {canReassign && movesWithOriginal ? <p className="small muted">{t('owner.movesWithOriginal')}</p> : null}
      {canReassign && !movesWithOriginal ? (
        <div>
          <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
            {t('owner.reassign')}
          </Button>
        </div>
      ) : null}
      <ReassignDialog open={open} current={owner} reassign={reassign} onClose={() => setOpen(false)} onReassigned={onReassigned} />
    </div>
  )
}

function ReassignDialog({
  open,
  current,
  reassign,
  onClose,
  onReassigned,
}: {
  open: boolean
  current: DocumentOwner
  reassign: (userId: string) => Promise<unknown>
  onClose: () => void
  onReassigned: () => void
}) {
  const { t } = useTranslation()
  // Asked when the dialog opens, and not before: an administrator who never opens it costs no request.
  // 200 is the users list's ceiling; a company with more Gestores than that would want a search here,
  // which Point 7 does not ask for.
  const gestores = useUsers({ role: 'GESTOR_INDIVIDUAL', perPage: 200 }, open)
  // The server admits an active or invited Gestor and no one else (`NOT_A_GESTOR`), so that is the list —
  // spelt as an allow-list, so that a status the contract gains later is not offered until someone has
  // decided it should be. Moving a document to its own Gestor changes nothing, so the current one is not
  // offered either.
  const candidates = (gestores.data?.data ?? []).filter(
    (u) => (u.status === 'ACTIVE' || u.status === 'INVITED') && u.id !== current.userId,
  )
  const [chosen, setChosen] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setChosen('')
      setError(null)
    }
  }, [open])

  // The dialog focuses the first field of its body as it opens. With the list still to come there is none,
  // so the focus waits on the close button and would stay there, a keypress from dismissing what the user
  // came to fill in: hand it to the select when the list arrives. Not `autoFocus`, which moves the focus
  // before the dialog notes where to return it on closing — on an open that finds the list already cached,
  // the focus would then come back to nothing.
  const choiceRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (open && gestores.isSuccess) choiceRef.current?.querySelector('select')?.focus()
  }, [open, gestores.isSuccess])

  async function submit() {
    if (!chosen) return
    setBusy(true)
    setError(null)
    try {
      await reassign(chosen)
      onReassigned()
      onClose()
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
      // That sentence tells the administrator to refresh the list and choose another, so the list is
      // refreshed: it is cached for 30 s, and the user just refused is still in it and still the choice.
      if (caught instanceof DecaApiError && caught.code === 'NOT_A_GESTOR') {
        setChosen('')
        void gestores.refetch()
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={t('owner.reassignTitle')}
      subtitle={t('owner.current', { name: current.display })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          {/* Not without the list on screen: a background refresh that fails hides the select, and the
              choice made from it must not stay sendable. */}
          <Button
            variant="primary"
            loading={busy}
            disabled={busy || chosen === '' || !gestores.isSuccess}
            onClick={() => void submit()}
          >
            {t('owner.reassign')}
          </Button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {gestores.isError ? <Notice tone="danger">{apiErrorMessage(gestores.error, t)}</Notice> : null}
      {/* The choice only once there is a list to choose from. Until then a select holding its placeholder
          alone is a control that can do nothing, and it would read as "there is nobody to choose". */}
      {gestores.isPending ? <p className="small muted">{t('common.loading')}</p> : null}
      {gestores.isSuccess ? (
        candidates.length === 0 ? (
          <Notice tone="info">{t('owner.noOtherGestor')}</Notice>
        ) : (
          <div className="form-grid form-grid--full" ref={choiceRef}>
            <SelectInput label={t('owner.newGestor')} value={chosen} onChange={(e) => setChosen(e.target.value)}>
              <option value="">{t('owner.choose')}</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>{`${u.fullName} (${u.email})`}</option>
              ))}
            </SelectInput>
          </div>
        )
      ) : null}
      <p className="small muted">{t('owner.reassignHelp')}</p>
    </Dialog>
  )
}
